import type { LangGraphMessage, IntermediateStep } from '@/types/langgraph';
import type { ChatMessage, CustomEventData } from '@/stores/slices/chat-slice';
import { v4 as uuidv4 } from 'uuid';
import { getContentString } from '@/components/thread/utils';

/**
 * Convert LangGraph messages to Redux ChatMessage format
 * Filters to only human and AI messages, skips tool messages
 */
export const convertLangGraphMessagesToRedux = (
  lgMessages: LangGraphMessage[]
): ChatMessage[] => {
  return lgMessages
    .filter((msg) => msg.type === 'human' || msg.type === 'ai')
    .map((msg) => ({
      id: msg.id || uuidv4(),
      content: getContentString(msg.content),
      role: msg.type === 'human' ? ('user' as const) : ('assistant' as const),
      timestamp: Date.now(),
      isStreaming: false,
    }));
};

/**
 * Reconstruct IntermediateStep entries from a historical LangGraph message list.
 *
 * Live streaming populates `intermediateSteps` from `msg.tool_calls` (see
 * `langgraph-client-v2.ts`), but `getThreadHistory` only returns the message
 * list; the steps need to be rebuilt so that tool-rendering components (e.g.
 * the api_key_submit_ui form) can still resolve their originating tool call
 * via `getLastToolCall()` after a page refresh or thread switch.
 *
 * We mirror the same shape the live parser emits: id prefixed with `tool-`,
 * type `'tool_call'`, content `Calling <name>`, plus `toolName` and
 * `toolInput`. Timestamps fall back to "now" because LangGraph history
 * doesn't carry per-call timestamps.
 */
export const extractIntermediateStepsFromMessages = (
  lgMessages: LangGraphMessage[],
): IntermediateStep[] => {
  const steps: IntermediateStep[] = [];
  for (const msg of lgMessages) {
    if (msg.type !== 'ai') continue;
    // `tool_calls` is not in LangGraphMessage's TS type but is present at
    // runtime on AI messages returned by `state.values.messages`.
    const toolCalls = (msg as unknown as { tool_calls?: unknown[] }).tool_calls;
    if (!Array.isArray(toolCalls) || toolCalls.length === 0) continue;
    for (const raw of toolCalls) {
      const tc = raw as { id?: unknown; name?: unknown; args?: unknown };
      // Strict guards: both id and name MUST be non-empty strings, otherwise
      // the step would later resolve to `tool-null` / `name: ''` and break
      // the stream provider's tool-message validator. We skip the bad entry
      // (most often a malformed/partial tool_call chunk persisted in
      // LangGraph state) rather than silently let it through.
      if (typeof tc.id !== 'string' || tc.id.length === 0) continue;
      if (typeof tc.name !== 'string' || tc.name.length === 0) continue;
      steps.push({
        id: `tool-${tc.id}`,
        type: 'tool_call',
        timestamp: Date.now(),
        content: `Calling ${tc.name}`,
        toolName: tc.name,
        toolInput: tc.args,
      });
    }
  }
  return steps;
};

/**
 * Convert API UI events to Redux CustomEventData format
 * Filters to only 'ui' type events and validates required fields
 */
export const convertApiEventsToRedux = (apiEvents: any[]): CustomEventData[] => {
  if (!Array.isArray(apiEvents)) {
    console.warn('⚠️ [EVENT-CONVERT] uiEvents is not an array:', apiEvents);
    return [];
  }

  return apiEvents
    .filter((event) => {
      // Only process UI type events
      if (event.type !== 'ui') return false;

      // Validate required fields
      if (!event.id || !event.name) {
        console.warn('⚠️ [EVENT-CONVERT] Invalid event missing id/name:', event);
        return false;
      }

      return true;
    })
    .map((event) => ({
      type: 'ui' as const,
      timestamp: Date.now(),
      id: event.id,
      name: event.name,
      props: event.props || {},
      metadata: event.metadata || {},
    }));
};
